import { useState, useMemo } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { format, parseISO } from "date-fns";
import EntityInformationModal from "../../components/modals/EntityInformationModal";
import { routeProgressLabel } from "../../utils";
import { LoaderCircle, Pencil } from "lucide-react";

// Work hours are seconds from midnight, the same encoding the Shifts screen uses.
const formatWorkTime = (sec) => {
    if (sec == null) return "-";
    const d = new Date(0);
    d.setSeconds(sec);
    let h = d.getUTCHours();
    const m = d.getUTCMinutes().toString().padStart(2, "0");
    const ampm = h >= 12 ? "pm" : "am";
    h = h % 12 || 12;
    return `${h}:${m} ${ampm}`;
};

const JobSchedulesCalendar = ({
    jobSchedules,
    onRefresh,
    filterText = "",
    onEditJob,
}) => {
    const [modalJob, setModalJob] = useState(null);
    const [currentView, setCurrentView] = useState("dayGridMonth");

    const getEventColor = (schedule) => {
        const now = new Date();
        const startDate = parseISO(schedule.start_time);
        const endDate = parseISO(schedule.end_time);
        if (now < startDate) return "#3b82f6";
        if (now > endDate) return "#6b7280";
        return "#10b981";
    };

    const calendarEvents = useMemo(() => {
        if (!jobSchedules) return [];

        const q = filterText?.trim().toLowerCase();
        const filteredSchedules = !q
            ? jobSchedules
            : jobSchedules.filter(
                  (schedule) =>
                      String(schedule.name || "")
                          .toLowerCase()
                          .includes(q) ||
                      String(schedule.description || "")
                          .toLowerCase()
                          .includes(q) ||
                      String(schedule.part?.name || "")
                          .toLowerCase()
                          .includes(q) ||
                      (schedule.operations || []).some((op) =>
                          String(op.operation?.name || "")
                              .toLowerCase()
                              .includes(q),
                      ) ||
                      String(schedule.device_id || "")
                          .toLowerCase()
                          .includes(q),
              );

        const events = [];

        jobSchedules.forEach((schedule) => {
            const start = parseISO(schedule.start_time);
            const end = parseISO(schedule.end_time);
            const color = getEventColor(schedule);

            if (currentView === "dayGridMonth") {
                events.push({
                    id: schedule.id.toString(),
                    title: `${schedule.device_id} - ${
                        schedule.part?.name || "N/A"
                    }`,
                    start: start.toISOString(),
                    end: end.toISOString(),
                    backgroundColor: color + "1A",
                    borderColor: color,
                    textColor: "#000000",
                    extendedProps: { schedule },
                });
            } else {
                const startDay = new Date(
                    start.getFullYear(),
                    start.getMonth(),
                    start.getDate(),
                );
                const endDay = new Date(
                    end.getFullYear(),
                    end.getMonth(),
                    end.getDate(),
                );
                const totalDays = Math.floor(
                    (endDay - startDay) / (1000 * 60 * 60 * 24),
                );

                events.push({
                    id: `${schedule.id}-start`,
                    title: `${schedule.device_id} - ${
                        schedule.part?.name || "N/A"
                    }`,
                    start: start.toISOString(),
                    end:
                        totalDays > 0
                            ? new Date(
                                  startDay.getFullYear(),
                                  startDay.getMonth(),
                                  startDay.getDate() + 1,
                              ).toISOString()
                            : end.toISOString(),
                    backgroundColor: color + "1A",
                    borderColor: color,
                    textColor: "#000000",
                    extendedProps: { schedule },
                });

                for (let i = 1; i < totalDays; i++) {
                    const midDay = new Date(startDay);
                    midDay.setDate(midDay.getDate() + i);
                    events.push({
                        id: `${schedule.id}-mid-${i}`,
                        title: `[Continues] ${schedule.device_id} - ${
                            schedule.part?.name || "N/A"
                        }`,
                        start: midDay.toISOString(),
                        backgroundColor: color + "1A",
                        allDay: true,
                        borderColor: color,
                        textColor: "#000000",
                        extendedProps: { schedule },
                    });
                }

                if (totalDays > 0) {
                    const endStart = new Date(endDay);
                    events.push({
                        id: `${schedule.id}-end`,
                        title: `${schedule.device_id} - ${
                            schedule.part?.name || "N/A"
                        }`,
                        start: endStart.toISOString(),
                        end: end.toISOString(),
                        backgroundColor: color + "1A",
                        borderColor: color,
                        textColor: "#000000",
                        extendedProps: { schedule },
                    });
                }
            }
        });

        return events;
    }, [jobSchedules, currentView, filterText]);

    const handleDateClick = (dateInfo) => {
        const clickedDate = dateInfo.date;
        const schedulesForDate = jobSchedules.filter((schedule) => {
            const startDate = parseISO(schedule.start_time);
            const endDate = parseISO(schedule.end_time);
            return clickedDate >= startDate && clickedDate <= endDate;
        });
    };

    const handleEventClick = (eventInfo) => {
        const schedule = eventInfo.event.extendedProps.schedule;
        setModalJob(schedule);
    };

    return (
        <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-3 tw-gap-6 tw-relative">
            <div className="lg:tw-col-span-3">
                <div className="tw-rounded-lg tw-border tw-bg-white tw-shadow-sm">
                    <div className="tw-p-6">
                        <FullCalendar
                            plugins={[
                                dayGridPlugin,
                                timeGridPlugin,
                                interactionPlugin,
                            ]}
                            initialView="dayGridMonth"
                            viewDidMount={(arg) =>
                                setCurrentView(arg.view.type)
                            }
                            headerToolbar={{
                                left: "prev,next today",
                                center: "title",
                                right: "dayGridMonth,timeGridWeek,timeGridDay",
                            }}
                            events={calendarEvents}
                            dateClick={handleDateClick}
                            eventClick={handleEventClick}
                            height="auto"
                            eventDisplay="block"
                            dayMaxEvents={3}
                            moreLinkClick="day"
                            selectable={true}
                            selectMirror={true}
                            eventAllDayDefault={false}
                            dayHeaderFormat={{ weekday: "short" }}
                            eventTimeFormat={{
                                hour: "numeric",
                                minute: "2-digit",
                                meridiem: "short",
                            }}
                        />
                    </div>
                </div>
            </div>
            <EntityInformationModal
                show={!!modalJob}
                onHide={() => setModalJob(null)}
                title={modalJob?.name}
                onEdit={() => {
                    if (onEditJob) {
                        onEditJob(modalJob);
                    }
                    setModalJob(null);
                }}
                isEditing={false}
                editTitle="Edit Job"
            >
                <div className="tw-flex tw-flex-row tw-row-span-2">
                    <div className="tw-flex-1">
                        <span className="tw-font-medium">Part:</span>{" "}
                        {modalJob?.part?.name}
                    </div>
                    <div className="tw-flex-1">
                        <span className="tw-font-medium">Progress:</span>{" "}
                        {routeProgressLabel(modalJob) || "No route"}
                    </div>
                </div>
                <div className="tw-flex tw-flex-row tw-row-span-2">
                    <div className="tw-flex-1">
                        <span className="tw-font-medium">From:</span>{" "}
                        {modalJob &&
                            format(parseISO(modalJob.start_time), "PPpp")}
                    </div>
                    <div className="tw-flex-1">
                        <span className="tw-font-medium">To:</span>{" "}
                        {modalJob &&
                            format(parseISO(modalJob.end_time), "PPpp")}
                    </div>
                </div>
                <div>
                    <span className="tw-font-medium">Shift:</span>{" "}
                    {modalJob?.shift
                        ? `${modalJob.shift.name} (${formatWorkTime(modalJob.shift.start_time)} - ${formatWorkTime(modalJob.shift.end_time)})`
                        : "Full range"}
                </div>
                {modalJob?.description && (
                    <div>
                        <span className="tw-font-medium">Description:</span>{" "}
                        {modalJob.description}
                    </div>
                )}
            </EntityInformationModal>
        </div>
    );
};

export default JobSchedulesCalendar;
